<script setup lang="ts">
import { useUserStore } from '~/store/user'
import type {
  MwBffAccountSelectionResponse,
  MwBffSessionResponse,
} from '~/types/mw-bff'

/**
 * Who is buying — the whole machine, inside the commit sheet.
 *
 * This used to render inline as the last card of the checkout, which put a
 * nine-field billing address at the bottom of an already long page. On a phone
 * that meant the most important button in the funnel sat immediately above a
 * fixed total bar and beside the floating assistant button, with the keyboard
 * up; and every sub-step of the wizard changed the page's height underneath the
 * customer. The card on the page is now just the email field and the social
 * buttons — the smallest possible ask — and everything that follows happens
 * here, on a surface that covers the page and has its own scroll.
 *
 * It owns the email as well as the branches, so there is ONE identity machine
 * rather than a field on the page and a state machine here that have to agree.
 * The card's job is to pre-fill it.
 *
 * Every path ends in one of two places: a session (→ `complete`), or a stage
 * that says why not. Nothing here navigates. Two-factor and account selection
 * used to hand off to `/login`, which tore the checkout down and threw the
 * customer out of the page at the moment they were committing — and that is not
 * a rare branch: WHMCS reports `twoFactorEnabled` for a client it has just
 * created, so it is on the ordinary "I already have an account" path.
 */
const props = defineProps<{
  /** Whatever the page's card collected. */
  initialEmail?: string
  /**
   * What a social provider answered — `code`, `register`, `selection` or
   * `error`. Handed down rather than read here, because the page is what
   * survives the popup being open.
   */
  socialParams?: Record<string, string> | null
}>()

const emit = defineEmits<{
  (e: 'complete', method: 'password' | 'social' | 'existing-account'): void
  /** Consumed — do not hand them to us again. */
  (e: 'socialHandled'): void
  /**
   * How far through the signup wizard, for the sheet's own rail. Null on every
   * other stage — a sub-count on "sign in" would be describing nothing.
   */
  (e: 'subProgress', progress: { index: number, total: number } | null): void
}>()

const { t: $t, te } = useI18n()
const userStore = useUserStore()
const { $mwApi } = useNuxtApp()
const { deviceId } = useDeviceId()
const toast = useToast()
const localePath = useLocalePath()
const route = useRoute()
const { bffFetch } = useMwBff()

/** Where a flow that still has to leave the page should come back to. */
const returnPath = computed(() => route.fullPath)

type Stage =
  | 'email'
  | 'signin'
  | 'taken'
  | 'register'
  | 'two-factor'
  | 'account'
  | 'social-error'
  | 'exchanging'
const stage = ref<Stage>('email')

/** Set when this signup is completing a provider hand-off rather than a form. */
const socialTicketId = ref<string | null>(null)
const socialFirstName = ref('')
const socialLastName = ref('')
const socialError = ref<string | null>(null)
const selectionToken = ref<string | null>(null)

const email = ref(props.initialEmail ?? '')
const password = ref('')
const checking = ref(false)
const submitting = ref(false)
const error = ref<string | null>(null)

/** Which call a chosen account has to be replayed through. */
type SelectionOrigin = 'credentials' | 'two-factor' | 'social'
const selectionOrigin = ref<SelectionOrigin>('credentials')
const challengeId = ref<string | null>(null)
const selectionAccounts = ref<MwBffAccountSelectionResponse['accounts']>([])

const emailValid = computed(() => /^\S+@\S+\.\S+$/.test(email.value.trim()))

// Only the signup wizard has sub-steps; leaving it clears the count so the
// sheet's rail does not keep reporting a position inside a form nobody is on.
watch(stage, (value) => {
  if (value !== 'register') emit('subProgress', null)
})

function onWizardStepChange(progress: { index: number, total: number }) {
  emit('subProgress', progress)
}

/**
 * Ask which of the three paths this address is on BEFORE showing any of them.
 *
 * `check-email` answers `{exists, canSignIn}` and both halves matter: WHMCS
 * holds an address as a LOGIN (`tblusers`) and as an account's contact address
 * (`tblclients`), and the two drift. `exists` blocks a signup either way,
 * because `AddClient` refuses both with a 409; `canSignIn` is what decides
 * whether offering to sign in is honest advice or a box that can never open.
 * Sending that third case to the signup form would spend a nine-field billing
 * address to earn a 409, which is the exact failure this step exists to remove.
 */
async function continueFromEmail() {
  if (!emailValid.value || checking.value) return
  checking.value = true
  error.value = null
  try {
    const res = await bffFetch<{ exists: boolean; canSignIn: boolean }>(
      '/whmcs/register/check-email',
      { method: 'POST', body: { email: email.value.trim() } },
    )
    if (!res?.exists) stage.value = 'register'
    else if (res?.canSignIn) stage.value = 'signin'
    else stage.value = 'taken'
  } catch {
    // A lookup that fails must not stop somebody buying: the registration
    // itself refuses a duplicate with a 409 and a real sentence.
    stage.value = 'register'
  } finally {
    checking.value = false
  }
}

/**
 * Everything a provider can answer, answered here.
 *
 * The desktop popup path used to navigate the MAIN window to
 * `/auth/social/callback` and back, so the checkout was destroyed and rebuilt
 * twice for what is, from the customer's point of view, one tap. A brand-new
 * social user had it worse still: their billing address was collected on the
 * auth layout, with no price and no order in sight.
 *
 * `socialHandled` is emitted BEFORE any await, because these tokens are
 * single-use and a second delivery must not spend them again.
 */
async function handleSocialParams(params: Record<string, string>) {
  emit('socialHandled')
  socialError.value = null
  try {
    if (params.error) {
      const key = `checkout.socialError.${params.error}`
      socialError.value = te(key) ? $t(key) : $t('checkout.socialError.generic')
      stage.value = 'social-error'
      return
    }
    if (params.register) {
      // A new account, from a verified provider identity: the same three
      // questions the email signup asks, from the same component — but with
      // the order on screen above them.
      socialTicketId.value = params.register
      if (params.email) email.value = params.email
      socialFirstName.value = params.firstName ?? ''
      socialLastName.value = params.lastName ?? ''
      stage.value = 'register'
      return
    }
    if (params.selection) {
      selectionToken.value = params.selection
      selectionOrigin.value = 'social'
      stage.value = 'account'
      selectionAccounts.value = (await userStore.getSocialSelectionAccounts(
        params.selection,
      )) as MwBffAccountSelectionResponse['accounts']
      return
    }
    if (params.code) {
      stage.value = 'exchanging'
      await userStore.exchangeSocialHandoff(params.code)
      await userStore.setUserDefaultLanguage()
      emit('complete', 'social')
      return
    }
  } catch {
    // A spent or expired ticket must read as something to retry, never as a
    // blank panel.
    socialError.value = $t('checkout.socialError.generic')
    stage.value = 'social-error'
  }
}

watch(
  () => props.socialParams,
  (params) => {
    if (params) void handleSocialParams(params)
  },
  { immediate: true },
)

/**
 * The card handed us an address, so answer the question it was asked for
 * without making the customer press Continue a second time. Skipped when a
 * provider hand-off is already in flight — that decides the stage.
 */
onMounted(() => {
  if (props.socialParams) return
  if (props.initialEmail && emailValid.value) void continueFromEmail()
})

/**
 * Send a reset link WITHOUT leaving the page.
 *
 * Recognising the address and then offering only a password field is a dead end
 * for anyone who cannot remember theirs: they have configured a whole server
 * and are one field from ordering. The response is deliberately identical
 * whether or not the address exists — we have already said it exists, via
 * `check-email`, so nothing new leaks, and keeping the wording generic is what
 * stops that changing by accident.
 */
const sendingReset = ref(false)
const resetSent = ref(false)

async function sendResetLink() {
  const address = email.value.trim()
  if (!address || sendingReset.value) return
  sendingReset.value = true
  try {
    // Through the BFF resolver: on the native app a relative `/api/…` never
    // leaves the device, and this is the one identity step the checkout can
    // take without a session.
    await bffFetch('/whmcs/password/reset', {
      method: 'POST',
      body: { email: address },
    })
  } catch {
    // The endpoint always answers success; show the same confirmation either
    // way so a failure cannot become an account-enumeration signal.
  } finally {
    sendingReset.value = false
    resetSent.value = true
  }
}

/** Back to the address field, cleared, so a fresh one can be tried. */
/**
 * Back to the address field, cleared, so a fresh one can be tried.
 *
 * Clears the PROVIDER state too, and that half is load-bearing rather than
 * tidy: this is also what "try again" does after a social error, and a
 * `socialTicketId` left behind would send the next email signup down the
 * `completeSocialRegistration` branch carrying a ticket that has already been
 * spent — a failure with no relation to anything the customer just did.
 */
function useDifferentEmail() {
  email.value = ''
  password.value = ''
  error.value = null
  resetSent.value = false
  challengeId.value = null
  selectionAccounts.value = []
  socialTicketId.value = null
  socialFirstName.value = ''
  socialLastName.value = ''
  socialError.value = null
  selectionToken.value = null
  selectionOrigin.value = 'credentials'
  stage.value = 'email'
}

function describeError(err: any, fallback: string): string {
  return err?.response?.data?.message ?? err?.data?.message ?? fallback
}

async function signIn() {
  if (!password.value || submitting.value) return
  submitting.value = true
  error.value = null
  try {
    const result = await userStore.login(email.value.trim(), password.value)
    if (result && 'result' in result) {
      if (result.result === 'two_factor_required') {
        challengeId.value = (result as any).challengeId
        selectionOrigin.value = 'credentials'
        stage.value = 'two-factor'
        return
      }
      if (result.result === 'account_selection_required') {
        selectionAccounts.value =
          (result as MwBffAccountSelectionResponse).accounts ?? []
        selectionOrigin.value = 'credentials'
        stage.value = 'account'
        return
      }
    }
    emit('complete', 'existing-account')
  } catch (err: any) {
    error.value = describeError(err, $t('page.login.invalid'))
  } finally {
    submitting.value = false
  }
}

/** <AuthTwoFactorStep> owns the code entry and the verify call. */
async function onFactorVerified() {
  // No `applySessionData` here — `verifyTwoFactor` has already set the session
  // on the store by the time this fires.
  await userStore.setUserDefaultLanguage()
  emit('complete', 'existing-account')
}

function onFactorAccountSelection(result: MwBffAccountSelectionResponse) {
  selectionAccounts.value = result.accounts ?? []
  selectionOrigin.value = 'two-factor'
  stage.value = 'account'
}

/**
 * Replay whatever produced the list, now with a client chosen.
 *
 * Deliberately duplicated from `login.vue` rather than shared: that page also
 * has a "remember me" to carry and a social leg this surface does not, and an
 * abstraction over three lines would cost more than it saves. If you change one
 * branch here, look at `onSubmitAccountSelection` there.
 */
async function onSelectAccount(clientId: number) {
  submitting.value = true
  error.value = null
  try {
    if (selectionOrigin.value === 'social' && selectionToken.value) {
      await userStore.selectSocialAccount(selectionToken.value, clientId)
      await userStore.setUserDefaultLanguage()
      emit('complete', 'social')
      return
    }
    const result =
      selectionOrigin.value === 'two-factor' && challengeId.value
        ? await userStore.verifyTwoFactor(
            challengeId.value,
            undefined,
            deviceId,
            false,
            clientId,
          )
        : await userStore.login(email.value.trim(), password.value, false, clientId)
    if (result && 'result' in result) {
      error.value = $t('page.login.invalid')
      return
    }
    await userStore.setUserDefaultLanguage()
    emit('complete', 'existing-account')
  } catch (err: any) {
    error.value = describeError(err, $t('page.login.invalid'))
  } finally {
    submitting.value = false
  }
}

/**
 * A signup ENDS SIGNED IN — the API mints the session from the fact that the
 * same request created the account, so there is no second credential check and
 * no emailed factor to clear.
 */
async function register(payload: Record<string, unknown>) {
  submitting.value = true
  error.value = null
  try {
    // Completing a provider hand-off is a different call: the email is already
    // verified by the provider, so there is no password login to establish and
    // the ticket is what proves who this is. `CheckoutSocialRegisterStep` puts
    // the ticket in the payload itself.
    if (socialTicketId.value) {
      await userStore.completeSocialRegistration(payload)
      await userStore.setUserDefaultLanguage()
      emit('complete', 'social')
      return
    }

    const response = await $mwApi.register({
      ...payload,
      email: email.value.trim(),
      password2: payload.password,
      deviceId,
      language: import.meta.client ? navigator.language : undefined,
    } as any)

    const session = response.data as unknown as MwBffSessionResponse
    if (session?.client && session?.sessionId) {
      await userStore.applySessionData(session)
      emit('complete', 'password')
      return
    }

    // No session came back (a WHMCS wobble between the create and the lookup).
    // The account exists, so finish on the login page, which owns the emailed
    // second factor. The configuration travels in the draft.
    toast.add({ title: $t('page.register.success'), color: 'success' })
    await navigateTo(
      localePath({ path: '/login', query: { redirect: returnPath.value } }),
    )
  } catch (err: any) {
    error.value = describeError(err, $t('page.register.failed'))
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div>
    <!-- Stage 1 — which path is this address on? Reached by opening the sheet
         without an address, or by asking to try a different one. -->
    <div v-if="stage === 'email'" class="space-y-4">
      <UFormField :label="$t('page.register.email')" name="flow-email">
        <UInput
          v-model="email"
          type="email"
          name="flow-email"
          autocomplete="email"
          size="xl"
          icon="i-heroicons-envelope"
          :placeholder="$t('page.register.emailDescription')"
          class="w-full"
          @keydown.enter.prevent="continueFromEmail"
        />
      </UFormField>

      <UButton
        size="lg"
        block
        :loading="checking"
        :disabled="!emailValid"
        :label="$t('billing.page.checkout.identity.continue')"
        @click="continueFromEmail"
      />

      <!-- The providers come AFTER the address here, so the login page's
           divider ("or continue with email") would point the wrong way. -->
      <div class="flex items-center gap-3 py-1">
        <div class="h-px flex-1 bg-default" />
        <span class="text-xs text-muted">{{ $t('billing.page.checkout.identity.orProvider') }}</span>
        <div class="h-px flex-1 bg-default" />
      </div>

      <AuthSocialLoginButtons :redirect="returnPath" />
    </div>

    <!-- Stage 2 — an account we recognise. -->
    <div v-else-if="stage === 'signin'" class="space-y-4">
      <p class="text-sm text-toned">
        {{ $t('billing.page.checkout.identity.knownEmail', { email }) }}
      </p>

      <UFormField :label="$t('page.login.password')" name="identity-password">
        <UInput
          v-model="password"
          type="password"
          name="identity-password"
          autocomplete="current-password"
          size="xl"
          icon="i-heroicons-lock-closed"
          class="w-full"
          @keydown.enter.prevent="signIn"
        />
      </UFormField>

      <UAlert v-if="error" color="error" variant="soft" :title="error" />

      <!-- Recovery, in place. -->
      <div class="flex items-center justify-between gap-2">
        <UButton
          variant="link"
          size="xs"
          class="px-0"
          :loading="sendingReset"
          :disabled="resetSent"
          :label="
            resetSent ? $t('page.login.resetSentLink') : $t('page.login.forgotPassword')
          "
          @click="sendResetLink"
        />
      </div>
      <p v-if="resetSent" role="status" class="-mt-2 text-xs leading-relaxed text-toned">
        {{ $t('page.login.resetSentBody', { email }) }}
        {{ $t('billing.page.checkout.identity.resetKeepsOrder') }}
      </p>

      <div class="flex gap-2">
        <UButton
          variant="ghost"
          :label="$t('billing.page.checkout.identity.useDifferentEmail')"
          @click="useDifferentEmail"
        />
        <UButton
          class="flex-1 justify-center"
          size="lg"
          :loading="submitting"
          :disabled="!password"
          :label="$t('billing.page.checkout.identity.signInAndContinue')"
          @click="signIn"
        />
      </div>
    </div>

    <!-- Stage 2b — the address is on an account but cannot sign in. A password
         box here would be one that can never open, and the signup form would
         spend the whole address form to earn a 409. -->
    <div v-else-if="stage === 'taken'" class="space-y-4">
      <UAlert
        color="warning"
        variant="soft"
        icon="i-lucide-circle-alert"
        :title="$t('billing.page.checkout.identity.takenTitle')"
        :description="$t('billing.page.checkout.identity.takenBody', { email })"
      />
      <UButton
        variant="outline"
        block
        :label="$t('billing.page.checkout.identity.useDifferentEmail')"
        @click="useDifferentEmail"
      />
    </div>

    <!-- Stage 2c — the emailed second factor, HERE rather than on /login. -->
    <AuthTwoFactorStep
      v-else-if="stage === 'two-factor' && challengeId"
      :email="email"
      :challenge-id="challengeId"
      :back-label="$t('billing.page.checkout.identity.backToSignIn')"
      @verified="onFactorVerified"
      @account-selection="onFactorAccountSelection"
      @error="(message: string) => (error = message)"
      @back="stage = 'signin'"
    />

    <!-- Stage 2d — more than one account to bill. -->
    <div v-else-if="stage === 'account'" class="space-y-3">
      <p class="text-sm text-toned">
        {{ $t('billing.page.checkout.identity.chooseAccount') }}
      </p>
      <AuthAccountSelectionStep
        :accounts="selectionAccounts"
        :loading="submitting"
        :error="error"
        show-back
        :back-label="$t('billing.page.checkout.identity.backToSignIn')"
        @select="onSelectAccount"
        @back="stage = 'signin'"
      />
    </div>

    <!-- Stage 2e — the provider said no, or the ticket is spent. The
         configuration is untouched on the page behind this. -->
    <div v-else-if="stage === 'social-error'" class="space-y-4">
      <UAlert
        color="error"
        variant="soft"
        icon="i-lucide-circle-alert"
        :title="$t('billing.page.checkout.identity.socialErrorTitle')"
        :description="socialError ?? undefined"
      />
      <UButton
        variant="outline"
        block
        :label="$t('billing.page.checkout.identity.socialErrorRetry')"
        @click="useDifferentEmail"
      />
    </div>

    <!-- Stage 2f — redeeming the provider's hand-off code. Seconds, but it is
         a request, and a blank panel would read as a dead one. -->
    <div
      v-else-if="stage === 'exchanging'"
      class="flex items-center gap-3 py-6 text-sm text-muted"
      role="status"
    >
      <UIcon name="i-lucide-loader-circle" class="size-5 animate-spin" />
      {{ $t('billing.page.checkout.identity.signingIn') }}
    </div>

    <!-- Stage 3 — a new account. The same three questions the social callback
         asks, from the same component, so there is one billing address form in
         this codebase rather than three. -->
    <CheckoutSocialRegisterStep
      v-else
      :ticket-id="socialTicketId ?? undefined"
      :email="email"
      :first-name="socialFirstName"
      :last-name="socialLastName"
      :loading="submitting"
      :error="error ?? undefined"
      chrome-less
      :title="$t('billing.page.checkout.identity.registerTitle')"
      :subtitle="$t('billing.page.checkout.identity.registerSubtitle')"
      @submit="register"
      @step-change="onWizardStepChange"
    />
  </div>
</template>
